import React from "react"
import classnames from "classnames"
import { useSelector } from "react-redux"
import { ReactComponent as EditIcon } from "../../images/icons/edit.svg"
import AccountSettingsForm from "../../forms/AccountSettingsForm"
import AddAdminForm from "../../forms/AddAdminForm"
import EmailPending from "./email-pending"
import services from "../../services"
import { toast } from "react-toastify"
import ToastInfo from "../../components/ToastContents/ToastInfo"
import { COMPANY_ADMIN, ASSOCIATE_COMPANY_ADMIN } from "../../helpers/userRoles"
import styles from "./index.module.scss"

const AccountSettingsContainer = ({ data, setSettings, refreshSettings }) => {
  const userInfo = useSelector((state) => state.user)
  // Both admin_one and admin_two can see admin fields
  const isCompanyAdmin = userInfo?.role === COMPANY_ADMIN || userInfo?.role === ASSOCIATE_COMPANY_ADMIN
  // Both admin_one and admin_two can manage additional admin invites
  const canAddAdmin = userInfo?.role === COMPANY_ADMIN || userInfo?.role === ASSOCIATE_COMPANY_ADMIN
  const [openedForm, openForm] = React.useState(null)
  const [openedAdminForm, setOpenedAdminForm] = React.useState(null)
  const [emailPending, setEmailPendingStatus] = React.useState(
    data.email_pending,
  )
  const [resendLimitStatus, setResendLimitStatus] = React.useState(
    data.resend_limit,
  )

  React.useEffect(() => {
    setEmailPendingStatus(data.email_pending)
    setResendLimitStatus(data.resend_limit)
  }, [data])

  const settings = {
    email: {
      label: "Email",
      value: data.email,
    },
    password: {
      label: "Password",
      value: "",
    },
    phone_number: {
      label: "Phone Number",
      value: data.phone_number,
    },
  }

  const adminSettings = {
    admin_1: {
      label: "Admin 1",
      value: data.admin_1,
    },
    admin_2: {
      label: "Admin 2",
      value: data.admin_2,
    },
  }

  const isAdmin2Confirmed = Boolean(data?.admin_2_confirmed)
  const admin2Id = data?.admin_2_id

  const handleAdmin2Save = (adminData) =>
    new Promise((res, rej) => {
      const request = admin2Id && !isAdmin2Confirmed
        ? services.accountServices.updateAdmin(admin2Id, adminData)
        : services.accountServices.addAdmin(adminData)

      request
        .then(({ data }) => {
          // Show success message (add vs update)
          const msg = admin2Id && !isAdmin2Confirmed
            ? "Admin invitation updated successfully. A new invitation email has been sent."
            : "Admin added successfully. An invitation email has been sent to confirm the admin account."

          toast.success(<ToastInfo>{msg}</ToastInfo>, {
            position: toast.POSITION.TOP_RIGHT,
          })
          
          // Refresh settings data to show updated admin list
          if (refreshSettings) {
            refreshSettings()
            setTimeout(() => {
              res(data)
            }, 500)
          } else {
            services.accountServices
              .getAccountSettings()
              .then(({ data: newData }) => {
                setEmailPendingStatus(newData.email_pending)
                setResendLimitStatus(newData.resend_limit)
                if (setSettings) {
                  setSettings(newData)
                }
                res(newData)
              })
              .catch((err) => {
                console.error("Error refreshing settings:", err)
                // Still resolve since admin was added successfully
                res(data)
              })
          }
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.error || error?.response?.data?.errors?.[0] || error?.message || "Failed to add admin. Please try again."
          toast.error(<ToastInfo>{errorMessage}</ToastInfo>, {
            position: toast.POSITION.TOP_RIGHT,
          })
          rej(error?.response?.data || { error: errorMessage })
        })
    })

  const resendEmail = () => {
    services.accountServices.resendEmail().catch(() => {
      setResendLimitStatus(true)
      showToast()
    })
  }

  const showToast = () => {
    toast.info(<ToastInfo>Please try again later</ToastInfo>, {
      position: toast.POSITION.TOP_RIGHT,
    })
  }

  const clearToken = () =>
    services.accountServices.clearToken().then(() => {
      setEmailPendingStatus(false)
    })

  const hideDetail = ({ detail, type }) => {
    let res = null

    if (detail) {
      if (type === "email") {
        const emailArray = detail.split("@")
        if (emailArray[0].length > 2) {
          const partToHide = emailArray[0].slice(1, emailArray[0].length - 2)
          res = detail.replace(partToHide, "******")
        } else {
          res = detail
        }
      } else if (type === "phone_number") {
        if (detail.length > 2) {
          const partToHide = detail.slice(1, detail.length - 2)
          res = detail.replace(partToHide, "**********")
        } else {
          res = detail
        }
      } else {
        res = "**********"
      }
    } else if (type === "password") {
      res = "**********"
    }

    return res
  }

  return (
    <div className={styles.settingsWrap}>
      <h1 className={styles.settingsTitle}>Account Settings</h1>

      <div className={styles.settingsContent}>
        <div className={styles.settingsTable}>
          {Object.keys(settings).map((key) =>
            key === "email" && emailPending ? (
              <EmailPending
                key={key}
                resendEmail={resendEmail}
                clearToken={clearToken}
                resendLimitStatus={resendLimitStatus}
                newEmail={hideDetail({
                  detail: emailPending,
                  type: "email",
                })}
              />
            ) : (
              <div key={key} className={styles.settingsTableRow}>
                <div className={styles.settingsTableCol}>
                  <div className={styles.settingsLabel}>
                    {settings[key].label}
                  </div>
                </div>

                <div className={styles.settingsTableCol}>
                  {openedForm === key ? (
                    <AccountSettingsForm
                      data={settings}
                      type={key}
                      onConfirm={setSettings}
                      closeForm={() => openForm(null)}
                    />
                  ) : (
                    <div className={styles.settingsValueWrapper}>
                      {hideDetail({
                        detail: settings[key].value,
                        type: key,
                      }) ? (
                        <div className={styles.settingsValue}>
                          {hideDetail({
                            detail: settings[key].value,
                            type: key,
                          })}
                        </div>
                      ) : (
                        <div
                          onClick={() => openForm(key)}
                          className={classnames(
                            styles.settingsAddNew,
                            styles.settingsValue,
                          )}
                        >
                          Add +
                        </div>
                      )}
                      {settings[key].value !== null && hideDetail({
                        detail: settings[key].value,
                        type: key,
                      }) && (
                        <div
                          className={styles.settingsEditBtn}
                          onClick={() => {
                            if (key === "email" && resendLimitStatus) {
                              showToast()
                            } else {
                              openForm(key)
                            }
                          }}
                        >
                          <EditIcon />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ),
          )}
          {isCompanyAdmin &&
            Object.keys(adminSettings).map((key) => (
              <div key={key} className={styles.settingsTableRow}>
                <div className={styles.settingsTableCol}>
                  <div className={styles.settingsLabel}>
                    {adminSettings[key].label}
                  </div>
                </div>

                <div className={styles.settingsTableCol}>
                  {openedAdminForm === key ? (
                    <AddAdminForm
                      onConfirm={key === "admin_2" ? handleAdmin2Save : undefined}
                      closeForm={() => {
                        setOpenedAdminForm(null)
                      }}
                    />
                  ) : (
                    <div className={styles.settingsValueWrapper}>
                      {hideDetail({
                        detail: adminSettings[key].value,
                        type: "email",
                      }) ? (
                        <div className={styles.settingsValue}>
                          {hideDetail({
                            detail: adminSettings[key].value,
                            type: "email",
                          })}
                        </div>
                      ) : (
                        // Only show "Add +" button if user can add admins (admin_one only)
                        canAddAdmin && key === "admin_2" && (
                          <div
                            onClick={() => setOpenedAdminForm(key)}
                            className={classnames(
                              styles.settingsAddNew,
                              styles.settingsValue,
                            )}
                          >
                            Add +
                          </div>
                        )
                      )}
                      {key === "admin_2" &&
                       adminSettings[key].value !== null && 
                       hideDetail({
                         detail: adminSettings[key].value,
                         type: "email",
                       }) &&
                       canAddAdmin &&
                       !isAdmin2Confirmed && (
                        <div
                          className={styles.settingsEditBtn}
                          onClick={() => {
                            setOpenedAdminForm(key)
                          }}
                        >
                          <EditIcon />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

export default AccountSettingsContainer
